import React from 'react';

function EloDetailsModal({ game, players, onClose }) {
  if (!game || !game.eloChanges) {
    return null;
  }

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '700px', maxHeight: '90vh', overflowY: 'auto' }}>
        <button className="modal-close" onClick={onClose}>✕</button>

        <h2 style={{ marginBottom: '10px' }}>ELO Calculation Details</h2>
        <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '20px' }}>
          {formatDate(game.timestamp)}
        </p>

        <div style={{ marginBottom: '20px', padding: '15px', background: 'rgba(100,100,100,0.2)', borderRadius: '8px' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Game Outcome</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', fontSize: '14px' }}>
            <div><strong>Result:</strong> {game.didWin ? 'Imposters Won' : 'Crewmates Won'}</div>
            <div><strong>Method:</strong> {game.outcomeType || 'Unknown'}</div>
            <div><strong>Players Remaining:</strong> {game.playersRemaining || 'N/A'}</div>
            <div><strong>Total Players:</strong> {game.eloMetadata?.lobbySize || game.totalPlayers || 'N/A'}</div>
            <div><strong>Game Length:</strong> {game.duration ? `${(game.duration / 60000).toFixed(1)} min` : 'N/A'}</div>
          </div>
        </div>

        <h3 style={{ margin: '0 0 10px 0', fontSize: '16px' }}>Player ELO Changes</h3>
        <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '20px' }}>
          {(() => {
            const modalMaxCrewTasks = game.eloMetadata?.maxCrewTasks ||
              (game.playerStats ? Math.max(0, ...game.playerStats.filter(p => !p.isImpostor).map(p => p.tasksCompleted || 0)) : 0);
            return game.eloChanges.map((change, idx) => {
              const eloChangeSign = change.eloChange >= 0 ? '+' : '';
              const changeColor = change.eloChange >= 0 ? '#4CAF50' : '#FF5252';
              const ps = game.playerStats && Array.isArray(game.playerStats)
                ? game.playerStats.find(p => p.playerName === change.name)
                : null;

              return (
                <div key={idx} style={{
                  padding: '8px 12px',
                  marginBottom: '6px',
                  background: change.won ? 'rgba(76, 175, 80, 0.1)' : 'rgba(255, 82, 82, 0.1)',
                  border: `1px solid ${change.won ? 'rgba(76, 175, 80, 0.3)' : 'rgba(255, 82, 82, 0.3)'}`,
                  borderRadius: '6px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '10px'
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '2px' }}>
                      <strong style={{ fontSize: '14px' }}>{change.name}</strong>
                      <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                        {change.role === 'imposter' ? '🔪 Imposter' : '🔵 Crewmate'}
                      </span>
                      <span style={{ fontSize: '12px' }}>{change.won ? '✅' : '❌'}</span>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)', display: 'flex', gap: '12px' }}>
                      {change.role === 'imposter' ? (
                        <span>Kills: {ps ? ps.kills : '?'}</span>
                      ) : (
                        <>
                          <span>Tasks: {ps ? `${ps.tasksCompleted}/${modalMaxCrewTasks}` : '?'}</span>
                          <span>{ps ? (ps.deathOrder > 0 ? `${ps.wasExiled ? 'Voted' : 'Death'}: #${ps.deathOrder}` : 'Survived') : '?'}</span>
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ fontSize: '15px', fontWeight: 'bold', color: changeColor }}>
                      {eloChangeSign}{change.eloChange.toFixed(1)}
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                      {change.oldElo.toFixed(0)} → {change.newElo.toFixed(0)}
                    </div>
                  </div>
                </div>
              );
            });
          })()}
        </div>

        {game.eloMetadata && (
          <div style={{ marginBottom: '20px', padding: '15px', background: 'rgba(65,105,225,0.1)', borderRadius: '8px', border: '2px solid rgba(65,105,225,0.3)' }}>
            <h3 style={{ margin: '0 0 15px 0', fontSize: '16px', color: '#6495ED' }}>📊 ELO Formula Breakdown</h3>

            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#FFD700' }}>Step 1: Historic Imposter Win Rate (HIWR)</h4>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                GIWR = {game.eloMetadata.giwr ? (game.eloMetadata.giwr * 100).toFixed(2) : 'N/A'}%
              </div>
              {game.eloMetadata.gameHistory && game.eloMetadata.gameHistory.length > 0 && (
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                  Recent Games (last 5): {game.eloMetadata.gameHistory.slice(0, 5).map(g => g ? '✓' : '✗').join(', ')}
                  <div style={{ fontSize: '11px', marginTop: '2px' }}>
                    Weights: [5, 4, 3, 2, 1] | Recent Avg = {game.eloMetadata.gameHistory.length > 0
                      ? ((game.eloMetadata.gameHistory.slice(0, 5).map((g, i) => g * (5 - i)).reduce((a, b) => a + b, 0) / 15) * 100).toFixed(2)
                      : 'N/A'}%
                  </div>
                </div>
              )}
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Formula: (3/4) × GIWR + (1/4) × Recent Average
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                HIWR = {(game.eloMetadata.hiwr * 100).toFixed(2)}%
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#FFD700' }}>Step 2: Expected Imposter Win Rate (EIWR)</h4>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Average Imposter ELO: {game.eloMetadata.avgImposterElo.toFixed(1)} | Average Crew ELO: {game.eloMetadata.avgCrewElo.toFixed(1)}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Skill Factor = 2^((AIE - ACE) / 400) = 2^({((game.eloMetadata.avgImposterElo - game.eloMetadata.avgCrewElo) / 400).toFixed(3)}) = {Math.pow(2, (game.eloMetadata.avgImposterElo - game.eloMetadata.avgCrewElo) / 400).toFixed(4)}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                EIWR = 1 / (1 / ((HIWR/(1-HIWR)) × Skill Factor) + 1)
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                EIWR = {(game.eloMetadata.eiwr * 100).toFixed(2)}%
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#FFD700' }}>Step 3: Raw Change (R)</h4>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                R = S - EIWR, where S = {game.didWin ? '1 (Imposters Won)' : '0 (Imposters Lost)'}
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                R = {game.eloMetadata.rawChange.toFixed(4)}
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#FFD700' }}>Step 4: K-Factor & Total Delta</h4>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                K-Factor = 28 × Lobby Size = 28 × {game.eloMetadata.lobbySize} = {game.eloMetadata.kFactor}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                Total Delta = R × Q × K = {game.eloMetadata.rawChange.toFixed(4)} × {game.eloMetadata.qualityMultiplier.toFixed(2)} × {game.eloMetadata.kFactor}
              </div>
              <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                Δ = {game.eloMetadata.totalDelta.toFixed(2)} ELO points
              </div>
            </div>

            <div style={{ marginBottom: '15px' }}>
              <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#FFD700' }}>Step 5: Individual Distribution</h4>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                <strong>Crewmates:</strong> Weight = S × D × T, share = Weight / Σ(Weights) × |Δ|
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                <strong>Imposters:</strong> Weight = S × B, share = Weight / Σ(Weights) × |Δ|
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-secondary)', padding: '8px', background: 'rgba(255,215,0,0.05)', borderRadius: '4px' }}>
                {game.eloChanges && game.eloChanges.length > 0 ? (() => {
                  const impostersWon = game.didWin;

                  // Build playerStats lookup by name
                  const statsMap = {};
                  if (game.playerStats && Array.isArray(game.playerStats)) {
                    game.playerStats.forEach(ps => { statsMap[ps.playerName] = ps; });
                  }

                  // Separate crewmates and imposters from eloChanges
                  const crewChanges = game.eloChanges.filter(c => c.role !== 'imposter');
                  const impChanges = game.eloChanges.filter(c => c.role === 'imposter');

                  // Re-rank crewmate death order (crewmate-only, same as eloSystem.js)
                  const crewWithStats = crewChanges.map(c => ({ ...c, ps: statsMap[c.name] || null }));
                  const deadCrew = crewWithStats
                    .filter(c => c.ps && (c.ps.deathOrder || 0) > 0)
                    .sort((a, b) => a.ps.deathOrder - b.ps.deathOrder);
                  const crewDeathRank = {};
                  deadCrew.forEach((c, idx) => { crewDeathRank[c.name] = idx + 1; });
                  const totalCrewmates = crewChanges.length;

                  // Calculate S for each player
                  // Winners: lower ELO players gain more (avgElo - player.elo)
                  // Losers: higher ELO players lose more (player.elo - avgElo)
                  const calcS = (change) => {
                    const avgElo = change.role === 'imposter' ? game.eloMetadata.avgImposterElo : game.eloMetadata.avgCrewElo;
                    const isPlayerWinning = change.won;
                    const exponent = isPlayerWinning
                      ? (avgElo - change.oldElo) / 400
                      : (change.oldElo - avgElo) / 400;
                    return Math.pow(1.1, exponent);
                  };

                  // Calculate D for crewmate
                  const calcD = (change) => {
                    const rank = crewDeathRank[change.name] || 0;
                    if (rank === 0) return 1; // survived
                    const ratio = rank / totalCrewmates;
                    const ps = statsMap[change.name];
                    if (ps && ps.wasExiled) return Math.pow(ratio, 0.25);
                    return Math.pow(ratio, 0.5);
                  };

                  // Calculate T for crewmate (only when imposters won)
                  // totalTasks is derived as the max tasksCompleted by any crewmate in this game,
                  // since the plugin cannot read TotalTasks from the API. Works retroactively.
                  const maxCrewTasks = game.eloMetadata?.maxCrewTasks ||
                    (game.playerStats ? Math.max(0, ...game.playerStats.filter(p => !p.isImpostor).map(p => p.tasksCompleted || 0)) : 0);
                  const calcT = (change) => {
                    if (!impostersWon) return null; // T not used when crew wins
                    if (maxCrewTasks === 0) return null; // no task data available
                    const ps = statsMap[change.name];
                    if (!ps) return 1;
                    const tr = Math.max(0, maxCrewTasks - (ps.tasksCompleted || 0));
                    if (tr === 0) return 1;
                    // Count how many crewmates have unfinished tasks
                    const f = crewChanges.filter(c => {
                      const cps = statsMap[c.name];
                      if (!cps) return false;
                      return maxCrewTasks - (cps.tasksCompleted || 0) > 0;
                    }).length;
                    if (f === 0) return 1;
                    return Math.pow(1 + 0.5 / f, Math.pow(tr, 0.25));
                  };

                  // Calculate B for imposter
                  const calcB = (change) => {
                    const ps = statsMap[change.name];
                    const kills = ps ? (ps.kills || 0) : 0;
                    return impostersWon ? Math.pow(1.1, kills) : Math.pow(1.1, -kills);
                  };

                  // Compute weights for normalization
                  const crewWeights = crewChanges.map(c => {
                    const s = calcS(c);
                    const d = calcD(c);
                    const t = calcT(c) ?? 1;
                    return { name: c.name, s, d, t, w: s * d * t };
                  });
                  const impWeights = impChanges.map(c => {
                    const s = calcS(c);
                    const b = calcB(c);
                    return { name: c.name, s, b, w: s * b };
                  });
                  const crewTotal = crewWeights.reduce((sum, x) => sum + x.w, 0);
                  const impTotal = impWeights.reduce((sum, x) => sum + x.w, 0);

                  const allChanges = game.eloChanges;
                  return (
                    <div>
                      {allChanges.map((change, idx) => {
                        const avgElo = change.role === 'imposter' ? game.eloMetadata.avgImposterElo : game.eloMetadata.avgCrewElo;
                        const exponent = change.won
                          ? (avgElo - change.oldElo) / 400
                          : (change.oldElo - avgElo) / 400;
                        const sValue = calcS(change);

                        if (change.role === 'imposter') {
                          const bInfo = impWeights.find(x => x.name === change.name);
                          const b = bInfo ? bInfo.b : 1;
                          const w = bInfo ? bInfo.w : sValue;
                          const sharePct = impTotal > 0 ? (w / impTotal * 100) : 0;
                          const ps = statsMap[change.name];
                          const kills = ps ? (ps.kills || 0) : 0;
                          return (
                            <div key={idx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: idx < allChanges.length - 1 ? '1px solid rgba(255,215,0,0.2)' : 'none' }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>{change.name} <span style={{ color: '#FF6B6B' }}>(Imposter)</span>:</div>
                              <div style={{ fontSize: '11px', marginLeft: '10px', lineHeight: '1.6' }}>
                                S = 1.1^(({change.won ? `${avgElo.toFixed(1)} − ${change.oldElo}` : `${change.oldElo} − ${avgElo.toFixed(1)}`}) / 400) = 1.1^({exponent.toFixed(3)}) = <strong>{sValue.toFixed(4)}</strong>
                                <br />B = 1.1^({impostersWon ? '' : '−'}{kills} kills) = <strong>{b.toFixed(4)}</strong>
                                <br />Weight = S × B = {sValue.toFixed(4)} × {b.toFixed(4)} = <strong>{w.toFixed(4)}</strong>
                                <br />Share = {w.toFixed(4)} / {impTotal.toFixed(4)} = {sharePct.toFixed(1)}% → <strong>{change.eloChange.toFixed(2)} ELO</strong>
                              </div>
                            </div>
                          );
                        } else {
                          const dValue = calcD(change);
                          const tRaw = calcT(change);
                          const tValue = tRaw ?? 1;
                          const w = sValue * dValue * tValue;
                          const sharePct = crewTotal > 0 ? (w / crewTotal * 100) : 0;
                          const rank = crewDeathRank[change.name] || 0;
                          const ps = statsMap[change.name];
                          const tr = ps ? Math.max(0, maxCrewTasks - (ps.tasksCompleted || 0)) : '?';
                          const dDesc = rank === 0 ? 'survived' : (ps && ps.wasExiled ? `voted out (rank ${rank}/${totalCrewmates})` : `killed (rank ${rank}/${totalCrewmates})`);
                          return (
                            <div key={idx} style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: idx < allChanges.length - 1 ? '1px solid rgba(255,215,0,0.2)' : 'none' }}>
                              <div style={{ fontWeight: 'bold', marginBottom: '3px' }}>{change.name} <span style={{ color: '#6495ED' }}>(Crewmate)</span>:</div>
                              <div style={{ fontSize: '11px', marginLeft: '10px', lineHeight: '1.6' }}>
                                S = 1.1^(({change.won ? `${avgElo.toFixed(1)} − ${change.oldElo}` : `${change.oldElo} − ${avgElo.toFixed(1)}`}) / 400) = 1.1^({exponent.toFixed(3)}) = <strong>{sValue.toFixed(4)}</strong>
                                <br />D = <strong>{dValue.toFixed(4)}</strong> ({dDesc})
                                {tRaw !== null && (
                                  <><br />T = <strong>{tValue.toFixed(4)}</strong> (tasks remaining: {tr})</>
                                )}
                                <br />Weight = S × D{tRaw !== null ? ' × T' : ''} = {sValue.toFixed(4)} × {dValue.toFixed(4)}{tRaw !== null ? ` × ${tValue.toFixed(4)}` : ''} = <strong>{w.toFixed(4)}</strong>
                                <br />Share = {w.toFixed(4)} / {crewTotal.toFixed(4)} = {sharePct.toFixed(1)}% → <strong>{change.eloChange.toFixed(2)} ELO</strong>
                              </div>
                            </div>
                          );
                        }
                      })}
                    </div>
                  );
                })() : (
                  <div>No individual distribution data available</div>
                )}
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
}

export default EloDetailsModal;
